<template>
    <section class="doc-section">

        <!-- Selector de Plataforma -->
        <div class="mb-6">
            <BotonesNavegacion @cambio-plataforma="plataformaActual = $event" />
        </div>

        <header class="header-container">
            <h1>Visualización de Dispositivos y Datos</h1>
            <p class="description">
                Vista que permite visualizar dispositivos registrados dentro del mapa y consultar
                la información relacionada a cada uno.
            </p>
        </header>

        <div class="two-col">
            <div class="col-steps">
                <p class="steps-label">Interacción en el mapa</p>

                <div class="step-card">
                    <div class="step-badge">1</div>
                    <div>
                        <div class="step-title">Buscar dispositivo en el mapa</div>
                        <div class="step-desc">En la vista de monitoreo se visualizan todos los dispositivos registrados representados por iconos.</div>
                    </div>
                </div>

                <div class="step-card">
                    <div class="step-badge">2</div>
                    <div>
                        <div class="step-title">Mostrar / ocultar datos</div>
                        <div class="step-desc">Haga clic sobre cualquier dispositivo para desplegar u ocultar su panel de información.</div>
                    </div>
                </div>
            </div>

            <div class="carousel-box">
                <transition name="fade" mode="out-in">
                    <CarruselAciqui
                        :key="plataformaActual"
                        :items="imagenesMonitoreoMostrar"
                    />
                </transition>
            </div>
        </div>

        <div class="form-grid">
            <div class="field-row">
                <span class="field-key">•</span>
                <div><div class="field-label">Identificador</div><div class="field-desc">Nombre único del dispositivo en el sistema</div></div>
            </div>
            <div class="field-row">
                <span class="field-key">•</span>
                <div><div class="field-label">Estado</div><div class="field-desc">Indica la condición actual de la unidad</div></div>
            </div>
            <div class="field-row">
                <span class="field-key">•</span>
                <div><div class="field-label">Ubicación</div><div class="field-desc">Dirección o coordenadas actuales</div></div>
            </div>
            <div class="field-row">
                <span class="field-key">•</span>
                <div><div class="field-label">Información Adicional</div><div class="field-desc">Datos técnicos configurados del equipo</div></div>
            </div>
        </div>

        <div class="doc-note note-warning">
            <svg class="note-icon" width="15" height="15" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                <path d="M8 5v3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                <circle cx="8" cy="10.5" r="0.75" fill="currentColor"/>
            </svg>
            <div class="note-body">
                <span class="note-label">Nota de datos</span>
                <p>La información mostrada dependerá de la configuración y disponibilidad de datos reportados por el dispositivo seleccionado.</p>
            </div>
        </div>

        <div class="section-title-bar mb-4">
            <h2>Método Alternativo</h2>
        </div>

        <div class="two-col alternate-flow">
            <div class="col-steps">
                <p class="steps-label">Búsqueda por Listado</p>
                
                <div class="timeline-container">
                    <div class="timeline-item">
                        <div class="timeline-dot">1</div>
                        <div class="timeline-content">
                            <div class="step-title">Dispositivos y ubicaciones</div>
                            <div class="step-desc">Acceda al apartado correspondiente en el menú lateral para abrir el listado.</div>
                        </div>
                    </div>

                    <div class="timeline-item">
                        <div class="timeline-dot">2</div>
                        <div class="timeline-content">
                            <div class="step-title">Seleccionar dispositivo</div>
                            <div class="step-desc">Ubique la unidad en la lista y presione el botón <strong>Ver</strong>.</div>
                        </div>
                    </div>

                    <div class="timeline-item">
                        <div class="timeline-dot">3</div>
                        <div class="timeline-content">
                            <div class="step-title">Enfoque en mapa</div>
                            <div class="step-desc">El mapa se desplazará automáticamente hacia la posición del activo.</div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-image">
                <transition name="fade" mode="out-in">
                    <CarruselAciqui
                        :key="plataformaActual"
                        :items="imagenesAlternativoMostrar"
                    />
                </transition>            
            </div>
        </div>

    </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import CarruselAciqui from '../Carruselimg.vue';
import BotonesNavegacion from '../BotonesNavegacion.vue';

const plataformaActual = ref('web');

/* ── Datos Web ───────────────────────────────────── */
const webData = {
    monitoreo: [
        {
            nombre: "Mapa de Monitoreo",
            url: '/img/datosDispositivos/Visualizar_Dispositivos_Mapa.png',
            descripcion: "Vista de dispositivos registrados en el mapa."
        },
        {
            nombre: "Panel de Datos",
            url: '/img/datosDispositivos/Mostrar_Datos_Dispositivo.png',
            descripcion: "Información detallada de la unidad seleccionada."
        }
    ],

    alternativo: [
        {
            nombre: "Listado de Dispositivos",
            url: '/img/datosDispositivos/Metodo_Alternativo_Dispositivo.png',
            descripcion: "Ubicación mediante el panel lateral de dispositivos."
        }
    ]
};

/* ── Datos Móvil ─────────────────────────────────── */
const mobileData = {
    monitoreo: [
        {
            nombre: "Mapa de Monitoreo",
            url: '/img/datosDispositivos/Visualizar_Dispositivos_Mapa_Movil.png',
            descripcion: "Vista de dispositivos registrados en el mapa."
        },
        {
            nombre: "Panel de Datos",
            url: '/img/datosDispositivos/Mostrar_Datos_Dispositivo_Movil.png',
            descripcion: "Información detallada de la unidad seleccionada."
        }
    ],

    alternativo: [
        {
            nombre: "Listado de Dispositivos",
            url: '/img/datosDispositivos/Metodo_Alternativo_Dispositivo_Movil.png',
            descripcion: "Ubicación mediante el panel lateral de dispositivos."
        },
        {
            nombre: "Listado de Dispositivos",
            url: '/img/datosDispositivos/Metodo_Alternativo_Dispositivo_Movil 2.png',
            descripcion: "Ubicación mediante el panel lateral de dispositivos."
        },
        {
            nombre: "Listado de Dispositivos",
            url: '/img/datosDispositivos/Metodo_Alternativo_Dispositivo_Movil 3.png',
            descripcion: "Ubicación mediante el panel lateral de dispositivos."
        }
    ]
};

/* ── Computed ────────────────────────────────────── */
const imagenesMonitoreoMostrar = computed(() =>
    plataformaActual.value === 'web'
        ? webData.monitoreo
        : mobileData.monitoreo
);

const imagenesAlternativoMostrar = computed(() =>
    plataformaActual.value === 'web'
        ? webData.alternativo
        : mobileData.alternativo
);
</script>

<style scoped>
/* Este es el contenedor del título */
.section-title-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px; /* Espacio debajo del título */
}

/* Este le da el color azul y el grosor */
.section-title-bar h2 {
    font-size: 20px;
    font-weight: 700;
    color: var(--primary-color); /* Aquí es donde toma el azul */
    margin: 0;
}

/* ── Estilos Base ─────────────────────────────────── */
.doc-section { padding: 20px; }
.description, .main-desc { font-size: 15px; opacity: 0.8; margin-bottom: 24px; line-height: 1.6; }

/* ── Layout dos columnas ──────────────────────────── */
.two-col { display: flex; gap: 32px; align-items: flex-start; margin-bottom: 32px; }
.col-steps { flex: 1; }
.col-image  { flex: 1; position: sticky; top: 20px; }

/* ── Cards y Soporte Modo Oscuro ───────────────────── */
.step-card {
    display: flex; gap: 14px; align-items: flex-start;
    background: var(--code-bg); border: 1px solid var(--border-color);
    border-radius: 10px; padding: 14px 16px; margin-bottom: 10px;
}
.step-badge {
    min-width: 28px; height: 28px; border-radius: 50%;
    background: var(--primary-color); color: #fff;
    font-size: 13px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

/* ── Timeline ─────────────────────────────────────── */
.timeline-container { position: relative; padding-left: 10px; margin-top: 10px; }
.timeline-container::before {
    content: ''; position: absolute; left: 23px; top: 10px; bottom: 10px;
    width: 2px; background: var(--border-color); opacity: 0.5;
}
.timeline-item { position: relative; display: flex; gap: 20px; margin-bottom: 25px; }
.timeline-dot {
    position: relative; z-index: 2; min-width: 28px; height: 28px; border-radius: 50%;
    background: var(--primary-color); color: white;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 800; box-shadow: 0 0 0 4px var(--bg-color);
}
.timeline-content {
    background: var(--code-bg); border: 1px solid var(--border-color);
    border-radius: 10px; padding: 12px 16px; flex: 1;
}

/* ── UI Elements ─────────────────────────────────── */
.section-divider { display: flex; align-items: center; gap: 12px; margin: 32px 0 16px; }
.section-divider h2 { font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.6; margin: 0; }
.divider-line { flex: 1; height: 1px; background: var(--border-color); }

/* Carrusel Box Adaptado a Modo Oscuro */
.carousel-box { 
    border: 1px solid var(--border-color); 
    padding: 8px; 
    border-radius: 10px; 
    background: var(--code-bg); /* Cambia automáticamente con el tema */
    margin-bottom: 16px; 
}

/* ── Grid y Notas ────────────────────────────────── */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 20px 0; }
.field-row { display: flex; gap: 10px; background: var(--code-bg); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 12px; }
.field-key { font-weight: 700; color: var(--primary-color); }
.field-label { font-weight: 600; font-size: 13px; }
.field-desc { font-size: 12px; opacity: 0.6; }

.doc-note { display: flex; gap: 12px; padding: 16px; border-radius: 8px; border: 1px solid; margin: 24px 0; font-size: 14px; }
.note-warning { background: var(--code-bg); border-color: #d97706; color: var(--text-color); }
.note-label { color: #d97706; font-weight: 700; text-transform: uppercase; font-size: 11px; display: block; }

.mb-6 {
    margin-bottom: 2rem;
}

/* ── Animación ───────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* ── Responsive ──────────────────────────────────── */
@media (max-width: 768px) {
    .two-col, .form-grid { flex-direction: column; grid-template-columns: 1fr; }
    .col-image { position: static; }
}
</style>